import { SmileIcon } from "lucide-react";
import { useCallback } from "react";
import { T, RecordProps, TLShape, TLAsset, TLBinding, Editor } from "tldraw";

import {
  BlockBody,
  BlockContainer,
  NodeBlockHeader,
  BlockContainerShapeContext,
} from "@/components/canvas-editor/components/Container";
import {
  CanvasPercentageInput,
  CanvasSimpleSelect,
  PercentOperatorSelect,
} from "@/components/canvas-editor/components/Inputs";
import {
  modelProbabilityExplanation,
  NodeDocs,
} from "@/components/canvas-editor/NodeDocs";
import {
  BindableUtil,
  createMigrations,
} from "@/components/canvas-editor/shapes/bindable";
import {
  SentimentAnalysisShape,
  sentimentAnalysisType,
  comparisonOperators,
  ComparisonOperator,
  SentimentAnalysisProps,
  sentimentAnalysisCategories,
  SentimentAnalysisCategory,
  metadataType,
  Dimensions,
} from "@/components/canvas-editor/shapes/shapes";
import { MIN_H, ToolStateNode } from "@/components/canvas-editor/utils";
import { SimpleTooltip } from "@/components/ui/tooltip";

export function isSentimentAnalysisShape(
  shape: TLAsset | TLBinding | TLShape,
): shape is SentimentAnalysisShape {
  return shape.type === sentimentAnalysisType;
}

export class SentimentAnalysisTool extends ToolStateNode {
  static override id = sentimentAnalysisType;

  static override uiToolDef = (editor: Editor) => ({
    id: sentimentAnalysisType,
    icon: SmileIcon,
    label: SentimentAnalysisShapeUtil.docs.title,
    defaultColor: SentimentAnalysisShapeUtil.docs.defaultColor,
    onSelect: () => editor.setCurrentTool(sentimentAnalysisType),
    docs: () => (
      <NodeDocs
        editor={editor}
        component={SentimentAnalysis}
        util={SentimentAnalysisShapeUtil}
      />
    ),
  });

  override onEnter() {
    this.editor.setCursor({ type: "none", rotation: 0 });
  }

  override onPointerDown() {
    const { currentPagePoint } = this.editor.inputs;

    this.editor.createShape({
      type: sentimentAnalysisType,
      x: currentPagePoint.x,
      y: currentPagePoint.y,
    });

    this.editor.setCurrentTool("select");
  }
}

export class SentimentAnalysisShapeUtil extends BindableUtil<SentimentAnalysisShape> {
  static override migrations = createMigrations(sentimentAnalysisType);
  static override type = sentimentAnalysisType;

  static override docs = {
    defaultColor: "green" as const,
    title: "Sentiment Analysis",
    description:
      "Use AI to analyze the sentiment of the content. This determines if the post has a positive, negative, or neutral tone.",
  };

  override getType() {
    return sentimentAnalysisType;
  }

  static override props: RecordProps<SentimentAnalysisShape> = {
    w: T.number,
    minW: T.number,
    isCollapsed: T.boolean.optional(),
    resultState: T.literalEnum("success", "failure", "idle").optional(),
    isReadOnly: T.boolean.optional(),
    h: T.number,
    sentimentAnalysisCategory: T.literalEnum(...sentimentAnalysisCategories),
    operator: T.literalEnum(...comparisonOperators),
    value: T.or(T.number, T.string),
    metadata: metadataType,
  };

  override getDefaultProps() {
    return {
      w: 360,
      minW: 400,
      h: MIN_H,
      sentimentAnalysisCategory: "Positive" as const,
      operator: ">=" as const,
      value: 0.5,
      metadata: undefined,
    };
  }

  override component(shape: SentimentAnalysisShape) {
    const selected = this.editor.getSelectedShapeIds();
    const isSelected = selected.includes(shape.id);

    return (
      <SentimentAnalysis
        shape={shape}
        updateShape={this.updateProps.bind(this)}
        isBound={this.getBoundToFeed(shape)}
        isSelected={isSelected}
      />
    );
  }
}

function SentimentAnalysis({
  isBound,
  shape,
  className,
  updateShape,
  isSelected,
}: {
  isSelected: boolean;
  isBound: boolean;
  shape: SentimentAnalysisShape;
  className?: string;
  updateShape: (
    shape: SentimentAnalysisShape,
    props: Partial<SentimentAnalysisProps>,
  ) => void;
}) {
  const onUpdateDimensions = useCallback(
    (dimensions: Pick<Dimensions, "h">) => {
      return updateShape(shape, dimensions);
    },
    [shape, updateShape],
  );

  return (
    <BlockContainerShapeContext value={shape}>
      <BlockContainer
        resultState={shape.props.resultState}
        pattern={shape.props.metadata?.pattern}
        isBound={isBound}
        className={className}
        variant={
          shape.props.metadata?.color ||
          SentimentAnalysisShapeUtil.docs.defaultColor
        }
        isSelected={isSelected}
        onUpdateDimensions={onUpdateDimensions}
        dynamicHeight={true}
      >
        <NodeBlockHeader
          props={shape.props}
          docs={SentimentAnalysisShapeUtil.docs}
        />
        <BlockBody>
          <CanvasSimpleSelect
            aria-label="Sentiment Analysis Category"
            options={sentimentAnalysisCategories}
            value={shape.props.sentimentAnalysisCategory}
            className="w-[160px] shrink-0"
            onValueChange={(v) =>
              updateShape(shape, {
                sentimentAnalysisCategory: v as SentimentAnalysisCategory,
              })
            }
            tooltip={SentimentAnalysisShapeUtil.docs.description}
          />
          <PercentOperatorSelect
            value={shape.props.operator}
            onValueChange={(v) =>
              updateShape(shape, { operator: v as ComparisonOperator })
            }
          />
          <SimpleTooltip message={modelProbabilityExplanation}>
            <CanvasPercentageInput
              aria-label="Threshold"
              value={shape.props.value as number}
              className="flex-1"
              placeholder="Threshold"
              onValueChange={(value) => updateShape(shape, { value })}
            />
          </SimpleTooltip>
        </BlockBody>
      </BlockContainer>
    </BlockContainerShapeContext>
  );
}
